import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { teacherAPI } from '../../services/api';
import './ExerciseAccess.css';

function ExerciseAccess({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedExercise, setSelectedExercise] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [accessRes, groupsRes] = await Promise.all([
        teacherAPI.getExerciseAccess(),
        teacherAPI.getGroups()
      ]);
      setData(accessRes.data);
      setGroups(groupsRes.data.groups);
      // Only set default exercise if none is selected
      if (accessRes.data.exercises.length > 0 && !selectedExercise) {
        setSelectedExercise(accessRes.data.exercises[0].id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForGroup = async (groupId) => {
    if (!selectedExercise) return;
    
    setError('');
    setSuccess('');
    
    try {
      await teacherAPI.openExerciseForGroup(groupId, selectedExercise);
      setSuccess('Exercise opened for group');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to open exercise');
    }
  };

  const handleCloseForAll = async () => {
    if (!selectedExercise) return;
    setShowConfirmModal(true);
  };

  const confirmCloseForAll = async () => {
    setShowConfirmModal(false);
    setError('');
    setSuccess('');
    
    try {
      const response = await teacherAPI.closeExerciseForAll(selectedExercise);
      setSuccess(`Exercise closed for all students (${response.data.count} students affected)`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close exercise');
    }
  };

  const handleToggleStudent = async (studentId, hasAccess) => {
    if (!selectedExercise) return;
    
    setError('');
    setSuccess('');
    
    try {
      if (hasAccess) {
        await teacherAPI.revokeExerciseAccess(studentId, selectedExercise);
        setSuccess('Access revoked');
      } else {
        await teacherAPI.openExerciseForStudent(studentId, selectedExercise);
        setSuccess('Access granted');
      }
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update access');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  if (!data || !data.exercises) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="error-message">Failed to load exercise data</div>
      </Layout>
    );
  }

  const currentExercise = data.exercises.find(ex => ex.id === selectedExercise);

  // Get short exercise name
  const getExerciseShortName = (title) => {
    const match = title.match(/Exercise (\d+)/i);
    return match ? `Exercise ${match[1]}` : title;
  };

  // Filter students based on search term and group
  const filteredStudents = data.students.filter(student => {
    const matchesSearch = student.full_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = selectedGroup === 'all' || student.group_id === parseInt(selectedGroup);
    return matchesSearch && matchesGroup;
  });

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="exercise-access">
        <h1>Exercise Access Control</h1>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {currentExercise && (
          <>
            {/* Combined top controls */}
            <div className="card">
              <div className="exercise-selector-group">
                <label htmlFor="exercise-select"><strong>Exercise:</strong></label>
                <select 
                  id="exercise-select"
                  value={selectedExercise} 
                  onChange={(e) => setSelectedExercise(e.target.value)}
                  className="exercise-select-inline"
                >
                  {data.exercises.map(ex => (
                    <option key={ex.id} value={ex.id}>{ex.title}</option>
                  ))}
                </select>
              </div>

              <div className="group-actions">
                {groups.map(group => (
                  <button
                    key={group.id}
                    className="btn btn-primary btn-sm"
                    onClick={() => handleOpenForGroup(group.id)}
                  >
                    Open for {group.name}
                  </button>
                ))}
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleCloseForAll}
                >
                  Close for All
                </button>
              </div>
            </div>

            {/* Student table with filters */}
            <div className="card">
              <div className="card-header-with-filters">
                <h2>Student Access</h2>
                <div className="filters-section">
                  <input
                    type="text"
                    placeholder="Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="group-filter"
                  >
                    <option value="all">All Groups</option>
                    {groups.map(group => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <table className="table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Group</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map(student => {
                    const exerciseAccess = student.exercises.find(ex => ex.exerciseId === selectedExercise);
                    const hasAccess = exerciseAccess?.hasAccess || false;
                    const completed = exerciseAccess?.completed || false;
                    const score = exerciseAccess?.score;
                    
                    return (
                      <tr key={student.id}>
                        <td>{student.full_name}</td>
                        <td>{student.group_name || '-'}</td>
                        <td>
                          {completed ? (
                            <span className="badge badge-success" title={`Score: ${score}%`}>
                              {score}% - Passed
                            </span>
                          ) : hasAccess ? (
                            <span className="badge badge-info">Available</span>
                          ) : (
                            <span className="badge badge-secondary">Locked</span>
                          )}
                        </td>
                        <td>
                          {completed ? (
                            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                              Completed
                            </span>
                          ) : (
                            <button
                              className={hasAccess ? "btn btn-danger btn-sm" : "btn btn-success btn-sm"}
                              onClick={() => handleToggleStudent(student.id, hasAccess)}
                            >
                              {hasAccess ? 'Revoke' : 'Grant'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Close Exercise for All Students?</h3>
              <p>This will revoke access from ALL students who currently have access to this exercise.</p>
              <p><strong>This action cannot be undone.</strong></p>
              <div className="modal-buttons">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={confirmCloseForAll}
                >
                  Yes, Close for All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ExerciseAccess;
